import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Table, TableBody, TableContainer, TableRow, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { SlimUser } from 'api';
import { AttioQueryKeys, FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT } from 'common/constants/analyst-dashboards';
import { EMPTY_VALUATIONS_TABLE } from 'common/constants/documents';
import { StyledTableCellEmptyMessage, useNoteHandler } from 'dashboard409a/components/ValuationsTable/common';
import { DealStageOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { useValuationsWithBulkData } from 'dashboard409a/hooks/useValuationsWithBulkData';
import { columns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { JobFilters } from 'dashboard409a/pages/Workboard/Filters';
import { getDealStatusOptions, getValuationStatusOptions, updateValuationDealStage } from 'dashboard409a/services/attio-common';
import { CreateNoteRequest, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import { getInitialColumnVisibilityFromStorage, saveColumnVisibilityToStorage } from 'dashboard409a/utilities/columnVisibilityStorage';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { Logger } from 'utilities/logger';
import ColumnToggler from './ColumnToggler/ColumnToggler';
import { EnhancedTableHeadColumn, ValuationsTableBulkDataProps } from './ValuationsTable.types';
import { StyledUserAutocomplete, userAutocompleteSlotProps } from './ValuationsTableBulkData.styles';
import ValuationsTableHead from './ValuationsTableHead';
import ValuationsTableRow from './ValuationsTableRow';

export const ValuationsTableBulkData = ({
  filters,
  orderBy,
  order,
  onRequestSort,
  onRowClick,
  sectionId
}: ValuationsTableBulkDataProps) => {
  const isInitialMount = useRef(true);
  const previousSectionId = useRef<string | undefined>(sectionId);
  
  const {
    rows,
    isLoading,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
    selectedUserEmail,
    setSelectedUserEmail,
    staffUsers,
    isLoadingStaffUsers,
  } = useValuationsWithBulkData({
    filters,
    orderBy,
    order,
  });

  const { isNoteLoading, noteError, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: (id, noteContent, createdNote) => {
      // If a note was just created, use the attio_valuation_id from the result
      if (createdNote?.attio_valuation_id) {
        const valuationId = createdNote.attio_valuation_id;
        updateValuationNotes(valuationId, noteContent);
      } else {
        // For updates, use the id parameter (which is the valuationId)
        updateValuationNotes(id as string, noteContent);
      }
    },
  });

  const handleNoteSave = useCallback(async (valuationId: string, noteContent: string, currentNote?: string) => {
    const request: CreateNoteRequest = {
      content: noteContent,
      attio_valuation_id: valuationId,
      reference_type: 'at',
    };

    const currentValuation = rows.find(r => r.record_id === valuationId);
    const hasExistingNote = Boolean(
      (currentNote && currentNote.trim() !== '' && currentNote !== '-') ||
      (currentValuation?.note?.content && currentValuation.note.content.trim() !== '')
    );

    let existingNoteId: string | undefined;
    
    if (hasExistingNote) {
      const noteId = currentValuation?.note?.id;
      if (noteId && noteId !== 0) {
        existingNoteId = noteId.toString();
      } else {
        existingNoteId = undefined;
      }
    }

    await handleNoteSaveGeneric(valuationId, noteContent, currentNote, request, hasExistingNote, existingNoteId);
  }, [rows, handleNoteSaveGeneric]);
  
  const getDefaultVisibility = useCallback((): Record<string, boolean> => {
    const initialVisibility: Record<string, boolean> = {};
    columns.forEach(col => {
      initialVisibility[col.id] = !FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT.includes(col.id);
    });
    return initialVisibility;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storageKey = sectionId ? `valuations_column_visibility_${sectionId}` : '';
  const [, setStoredVisibility] = useLocalStorage<Record<string, boolean>>(
    storageKey || 'temp_key',
    getDefaultVisibility()
  );

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    if (sectionId) {
      return getInitialColumnVisibilityFromStorage(sectionId, columns as EnhancedTableHeadColumn[]);
    }
    return getDefaultVisibility();
  });

  // Sync columnVisibility with storedVisibility when sectionId changes
  useEffect(() => {
    if (sectionId) {
      if (sectionId !== previousSectionId.current) {
        isInitialMount.current = true;
        previousSectionId.current = sectionId;
      }
      
      const savedVisibility = getInitialColumnVisibilityFromStorage(sectionId, columns as EnhancedTableHeadColumn[]);
      setColumnVisibility(savedVisibility);
    } else {
      setColumnVisibility(getDefaultVisibility());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, getDefaultVisibility]);

  // Update localStorage when columnVisibility changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (sectionId) {
      saveColumnVisibilityToStorage(sectionId, columnVisibility, setStoredVisibility);
    }
  }, [columnVisibility, sectionId, setStoredVisibility]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const handleColumnWidthChange = useCallback((columnId: string, newWidth: number) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth,
    }));
  }, []);

  const conditionalColumns = useMemo(() => columns.filter(col => columnVisibility[col.id] !== false), [columnVisibility]);

  const allRows = rows;

  const [filteredRows, setFilteredRows] = useState<SimplifiedAttioValuation[]>(allRows);

  useEffect(() => {
    setFilteredRows(allRows);
  }, [allRows]);

  const applyFilters = useCallback((filterState: {
      searchParam: string;
      searchStatus: string;
      fromDate: Date | null;
      toDate: Date | null;
    }) => {
    let filtered = [...allRows];
  
    if (filterState.searchParam.trim()) {
      const searchLower = filterState.searchParam.toLowerCase();
      filtered = filtered.filter(row => 
        row.company_name?.toLowerCase().includes(searchLower) ||
          row.valuation_fee?.toLowerCase().includes(searchLower) ||
          row.record_id?.toLowerCase().includes(searchLower)
      );
    }
  
    if (filterState.searchStatus !== 'default') {
      filtered = filtered.filter(row => row.status === filterState.searchStatus);
    }
  
    if (filterState.fromDate) {
      filtered = filtered.filter(row => {
        if (!row.valuation_date) {
          return false;
        }
        const fromDateEndOfDay = new Date(filterState.fromDate!);
        fromDateEndOfDay.setHours(23, 59, 59, 999);
        const rowDate = new Date(row.valuation_date);
        rowDate.setHours(23, 59, 59, 999);

        return rowDate >= fromDateEndOfDay;
      });
    }
  
    if (filterState.toDate) {
      filtered = filtered.filter(row => {
        if (!row.valuation_date) {
          return false;
        }
        const rowDate = new Date(row.valuation_date);
        const toDateEndOfDay = new Date(filterState.toDate!);
        rowDate.setHours(23, 59, 59, 999);
        toDateEndOfDay.setHours(23, 59, 59, 999);
        return rowDate < toDateEndOfDay;
      });
    }
  
    setFilteredRows(filtered);
  }, [allRows]);
  
  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const { data: dealStageOptionsData = [] } = useQuery<Array<{ id?: { status_id?: string }; title?: string }>>({
    queryKey: [AttioQueryKeys.DEAL_STAGE_OPTIONS],
    queryFn: async () => {
      const response = await getDealStatusOptions();
      return (response.data || []) as Array<{ id?: { status_id?: string }; title?: string }>;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const statusOptions = attioStatusOptions.map(option => ({
    id: {
      workspace_id: option.id.workspace_id,
      object_id: option.id.object_id,
      attribute_id: option.id.attribute_id,
      option_id: option.id.option_id,
    },
    title: option.title,
    is_archived: option.is_archived,
  }));
  const filterStatusOptions = statusOptions.filter(option => rows.some(row => row.status === option.title));
  const statusOptionsLoading = isLoadingStatusOptions;

  const user = useSelector((state: RootStateType) => state.user);

  const handleDealStageChange = useCallback(async (row: SimplifiedAttioValuation, dealStageId: string) => {
    if (!row.deal_id) {
      enqueueSnackbar('Cannot update deal stage: deal_id is missing', { variant: 'error' });
      return;
    }

    const dealId = row.deal_id;
    try {
      await updateValuationDealStage(dealId, dealStageId);
      
      // Find the deal stage label from the options (same structure as ValuationDealInfo expects)
      const selectedOption = dealStageOptionsData.find(option => option.id?.status_id === dealStageId);
      const newDealStage = selectedOption?.title || row.deal_stage;

      // Update local state
      setFilteredRows(prevRows => 
        prevRows.map(r => 
          r.record_id === row.record_id 
            ? { ...r, deal_stage: newDealStage }
            : r
        )
      );
    } catch (error) {
      Logger.error('Error updating deal stage', error);
    }
  }, [dealStageOptionsData]);

  const handleRowClick = (row: SimplifiedAttioValuation, event: React.MouseEvent<HTMLTableRowElement>) => {
    onRowClick(row, event);
  };
  
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const handleColumnsToggle = (columnId: string) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} sx={{ marginTop: 2, justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <JobFilters 
            statusOptions={filterStatusOptions} 
            statusOptionsLoading={statusOptionsLoading} 
            onFiltersChange={applyFilters}
          />
          <ColumnToggler 
            columns={columns as EnhancedTableHeadColumn[]} 
            columnVisibility={columnVisibility}
            onColumnsToggle={handleColumnsToggle} 
          />
          {user?.is_superuser && (
            <StyledUserAutocomplete
              size="small"
              options={staffUsers}
              getOptionLabel={(option: SlimUser) => option.email || ''}
              isOptionEqualToValue={(option: SlimUser, value: SlimUser) => option.email === value.email}
              value={selectedUserEmail ? (staffUsers.find((staffUser: SlimUser) => staffUser.email === selectedUserEmail) || null) : null}
              onChange={(_event, newValue: SlimUser | null) => {
                setSelectedUserEmail(newValue?.email || '');
              }}
              disabled={isLoadingStaffUsers}
              slotProps={userAutocompleteSlotProps}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Select User"
                />
              )}
            />
          )}
        </Box>
        <Box marginRight={2}>
          <Typography variant="body1">Total valuations: {filteredRows.length}</Typography>
        </Box>
      </Box>
      <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table sx={{ minWidth: 650 }}>
          <ValuationsTableHead
            orderBy={orderBy}
            order={order}
            onRequestSort={onRequestSort}
            columns={conditionalColumns as EnhancedTableHeadColumn[]}
            columnWidths={columnWidths}
            onColumnWidthChange={handleColumnWidthChange}
          />
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <StyledTableCellEmptyMessage colSpan={conditionalColumns.length + 3}>{EMPTY_VALUATIONS_TABLE}</StyledTableCellEmptyMessage>
              </TableRow>
            ) : (
              filteredRows.map(row => (
                <ValuationsTableRow
                  key={row.record_id}
                  valuationRow={row}
                  onRowClick={handleRowClick}
                  onStatusUpdate={updatedRow => updateValuationStatus(updatedRow.record_id, updatedRow.status || '')}
                  onNoteUpdate={updatedRow => updateValuationNotes(updatedRow.record_id, updatedRow.note?.content || '')}
                  onEngagementManagerUpdate={updateValuationEngagementManager}
                  onNoteSave={noteContent => {
                    const currentNoteContent = row.note?.content || '';
                    return handleNoteSave(row.record_id, noteContent, currentNoteContent);
                  }}
                  statusOptions={statusOptions}
                  statusOptionsLoading={statusOptionsLoading}
                  isNoteLoading={isNoteLoading}
                  noteError={noteError}
                  columns={conditionalColumns as EnhancedTableHeadColumn[]}
                  columnWidths={columnWidths}
                  onDealStageChange={async (dealStageId: string) => {
                    await handleDealStageChange(row, dealStageId);
                  }}
                  dealStageOptions={dealStageOptionsData as DealStageOption[]}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
